// Tableau d'alertes réutilisable (dashboard + page /alerts).
// Couleurs/labels des sévérités/catégories exposés pour réutilisation par
// les barres de filtres de la page Alertes.

import { ArrowRight, Check, Eye, EyeOff, Ticket } from 'lucide-react';
import { useDashboard } from '../views/dashboardContext.jsx';
import { useTableSort } from '../lib/useTableSort.jsx';
import { assignBon, buildAlertTaskText, ComitBonChip, ComitTaskButton } from '../lib/comit.jsx';
import { MachineTypeBadge } from './MachineTypeBadge.jsx';
import { fmtMachineType } from '../lib/format.jsx';

export const ALERT_SEV_COLORS = ['#3b82f6', '#f59e0b', '#ef4444'];
export const ALERT_SEV_LABELS = ['Info', 'Avertissement', 'Critique'];
// L'index EST la valeur persistée en base (alerts.Category) : miroir exact de
// l'enum AlertCategory dans Models/Entities.cs. Ajouter une catégorie = ajouter
// à la fin des deux côtés, jamais renuméroter.
export const ALERT_CAT_LABELS = [
  'Memoire',
  'Disque',
  'Mises a jour',
  'Connectivite',
  'GPU',
  'VRAM',
  'T° CPU',
  'T° GPU',
  // Préfixe SID sous Windows, /etc/machine-id sous Linux — même symptôme
  // (machine clonée sans généralisation), donc même catégorie.
  'ID machine duplique',
  'CVE',
  'CPU',
  'Conteneurs',
  'Sante disque',
  'Sauvegarde',
  'Acces distant',
  'Site web',
];

// Index de 'CVE' ci-dessus. Dérivé plutôt qu'écrit en dur (9) : si une
// catégorie était un jour insérée avant, un littéral pointerait silencieusement
// vers la mauvaise ligne, alors qu'ici le lien casse de façon visible.
const CAT_CVE = ALERT_CAT_LABELS.indexOf('CVE');

// Actions en icône seule : la colonne portait quatre libellés et débordait.
// Le sens passe par l'infobulle, obligatoire sur chacun de ces boutons.
const iconBtn = (couleur) =>
  `border border-rule-3 rounded-md bg-transparent cursor-pointer p-1 inline-flex items-center ${couleur}`;

export function AlertsTable({
  alerts,
  selected = [],
  toggleSelect,
  toggleAll,
  handleResolve,
  showSelect = true,
  showActions = true,
  sortCtl,
  // Appelée après une déliaison de bon ComIT, pour que l'appelant recharge.
  onBonChanged = null,
  // Ignorer / réactiver : gérés par la page, qui porte la saisie du motif et
  // le rechargement de la liste.
  onIgnore = null,
  onUnignore = null,
  // Ouvre la saisie d'un numéro de bon existant pour cette seule alerte.
  onAssignBon = null,
}) {
  // `goToVulns` peut être absent si la table est rendue hors du contexte
  // dashboard : le titre retombe alors en texte simple plutôt que de planter.
  const { navigate, goToVulns, can } = useDashboard();
  const canManage = can ? can('ManageAlerts') : false;

  const detachBon = async (id) => {
    await assignBon(0, { alertIds: [id] });
    onBonChanged && onBonChanged();
  };
  // Tri interne par défaut, mais l'appelant peut fournir son propre contrôleur
  // (`sortCtl`) pour que le tri survive à un démontage de la table — AlertsPage
  // la démonte à chaque chargement, ce qui réinitialisait le tri à chaque
  // changement de filtre ou de page.
  const ownSort = useTableSort('occurredAt', 'desc');
  const { sortRows, headerProps, arrow } = sortCtl || ownSort;

  const cols = [
    { key: 'severity', label: 'Severite' },
    { key: 'category', label: 'Categorie' },
    { key: 'computerName', label: 'Appareil' },
    // Le type vient de l'agent, pas de la ligne d'alerte : une machine reclassée
    // voit toutes ses alertes suivre, y compris celles déjà résolues.
    { key: 'machineType', label: 'Type' },
    { key: 'clientName', label: 'Client' },
    { key: 'title', label: 'Titre' },
    { key: 'currentValue', label: 'Valeur' },
    { key: 'occurredAt', label: 'Date' },
    { key: 'isResolved', label: 'Statut' },
  ];
  if (showActions) cols.push({ key: null, label: 'Actions' });

  const sorted = sortRows(alerts || [], (a, key) => {
    if (key === 'occurredAt') return a.occurredAt ? new Date(a.occurredAt) : null;
    if (key === 'isResolved') return a.isResolved ? 1 : 0;
    // Tri sur l'étiquette affichée : « Server » précède « Workstation » en
    // anglais quand « Serveur » suit « Poste de travail » en français.
    if (key === 'machineType') return fmtMachineType(a.machineType);
    return a[key];
  });

  return (
    <table className="w-full text-[12.5px] border-collapse">
      <thead>
        <tr className="border-b border-rule">
          {showSelect && (
            <th className="py-2.5 px-3.5 w-[30px]">
              <input
                type="checkbox"
                checked={selected.length === alerts.length && alerts.length > 0}
                onChange={toggleAll}
              />
            </th>
          )}
          {cols.map((c) => (
            <th
              key={c.label}
              className="py-2.5 px-3.5 text-left text-muted-2 font-medium text-[11px] uppercase whitespace-nowrap"
              {...headerProps(c.key)}
            >
              {c.label}
              {arrow(c.key)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((a) => {
          const sc = ALERT_SEV_COLORS[a.severity] || '#64748b';
          return (
            <tr
              key={a.id}
              className="border-b border-rule"
              style={{ background: a.isResolved ? 'transparent' : 'rgba(239,68,68,0.02)' }}
            >
              {showSelect && (
                <td className="py-2.5 px-3.5">
                  <input
                    type="checkbox"
                    checked={selected.includes(a.id)}
                    onChange={() => toggleSelect && toggleSelect(a.id)}
                  />
                </td>
              )}
              <td className="py-2.5 px-3.5">
                <span
                  className="rounded-md text-[10px] font-semibold px-2.5 py-0.5"
                  style={{ background: sc + '20', color: sc }}
                >
                  {ALERT_SEV_LABELS[a.severity] || '?'}
                </span>
              </td>
              <td className="py-2.5 px-3.5">
                <span className="rounded-md text-[10px] text-muted px-2 py-0.5 bg-slate-500/10">
                  {ALERT_CAT_LABELS[a.category] || '?'}
                </span>
              </td>
              <td
                className="py-2.5 px-3.5 text-blue-400 text-xs cursor-pointer font-mono"
                onClick={() => navigate('deviceDetail', { device: a.agentId })}
              >
                {a.computerName}
              </td>
              <td className="py-2.5 px-3.5">
                <MachineTypeBadge machineType={a.machineType} />
              </td>
              <td className="py-2.5 px-3.5 text-muted text-[11.5px]">{a.clientName || '—'}</td>
              {/* La colonne est tronquée à 300 px : l'infobulle porte le titre
                  complet ET le message détaillé, sinon un libellé comme
                  « AVERTISSEMENT : Santé disque Samsung SSD … » est illisible.
                  Elle est posée sur le <td>, donc valable pour TOUTES les
                  catégories, y compris quand le titre n'est pas cliquable.

                  Cas particulier CVE : l'alerte annonce un nombre ("1 critique,
                  5 élevées") sans dire QUELS logiciels — le titre mène donc à la
                  liste des vulnérabilités de cet appareil. */}
              <td
                className="py-2.5 px-3.5 text-fg-2 overflow-hidden truncate whitespace-nowrap max-w-[300px]"
                title={
                  a.title +
                  (a.message ? '\n\n' + a.message : '') +
                  (a.category === CAT_CVE && goToVulns
                    ? '\n\n▸ Cliquer pour voir les logiciels vulnérables'
                    : '')
                }
              >
                {a.category === CAT_CVE && goToVulns ? (
                  <span
                    onClick={() => goToVulns(null, a.agentId, a.computerName)}
                    className="text-blue-400 cursor-pointer underline decoration-dotted underline-offset-2 hover:text-blue-300"
                  >
                    {a.title}
                  </span>
                ) : (
                  a.title
                )}
              </td>
              <td className="py-2.5 px-3.5 font-mono text-[11px] text-muted">
                {a.currentValue != null ? a.currentValue.toFixed(1) + '%' : '—'}
              </td>
              <td className="py-2.5 px-3.5 text-muted text-[11px]">
                {new Date(a.occurredAt).toLocaleString('fr-FR')}
              </td>
              <td className="py-2.5 px-3.5">
                {a.isIgnored ? (
                  /* Distinguée d'une résolution : la condition n'a pas disparu,
                     elle a été acceptée. Le motif est la première chose à voir —
                     sans lui, personne n'ose réactiver l'alerte six mois plus tard. */
                  <span
                    className="text-[11px] inline-flex items-center gap-1 text-muted"
                    title={
                      'Ignorée' +
                      (a.ignoredBy ? ' par ' + a.ignoredBy : '') +
                      (a.ignoredAt ? ' le ' + new Date(a.ignoredAt).toLocaleString('fr-FR') : '') +
                      (a.ignoredReason ? '\n\nMotif : ' + a.ignoredReason : '')
                    }
                  >
                    <EyeOff size={11} strokeWidth={2} />
                    Ignorée
                  </span>
                ) : a.isResolved ? (
                  <span className="text-[11px] inline-flex items-center gap-1 text-ok">
                    <Check size={11} strokeWidth={2.5} />
                    {'Resolue' + (a.resolvedBy ? ' par ' + a.resolvedBy : '')}
                  </span>
                ) : (
                  <span className="text-red-300 text-[11px]">Active</span>
                )}
                {a.isIgnored && a.ignoredReason && (
                  <div className="text-[10.5px] text-muted-2 mt-0.5 max-w-[220px] truncate"
                       title={a.ignoredReason}>
                    {a.ignoredReason}
                  </div>
                )}
              </td>
              {showActions && (
                <td className="py-2.5 px-3.5">
                  <div className="flex gap-1.5 items-center">
                    {!a.isResolved && handleResolve && (
                      <button
                        onClick={() => handleResolve(a.id)}
                        title="Résoudre : la condition est corrigée"
                        className={iconBtn('text-emerald-300 hover:bg-ok/10')}
                      >
                        <Check size={14} strokeWidth={2.5} />
                      </button>
                    )}
                    {!a.isResolved && canManage && onIgnore && (
                      <button
                        onClick={() => onIgnore(a)}
                        title="Ignorer : la condition demeure, on ne veut plus en être averti (un motif sera demandé)"
                        className={iconBtn('text-muted hover:text-fg-2 hover:bg-card-hi')}
                      >
                        <EyeOff size={14} strokeWidth={2} />
                      </button>
                    )}
                    {a.isIgnored && canManage && onUnignore && (
                      <button
                        onClick={() => onUnignore(a)}
                        title="Réactiver : remettre cette alerte sous surveillance"
                        className={iconBtn('text-blue-400 hover:bg-blue-500/10')}
                      >
                        <Eye size={14} strokeWidth={2} />
                      </button>
                    )}
                    {/* Alerte déjà prise en charge : on montre le bon plutôt que
                        le bouton de création, sinon un second technicien
                        ouvrirait un second bon pour le même incident. */}
                    {a.comitBonNumero ? (
                      <ComitBonChip
                        numero={a.comitBonNumero}
                        linkedAt={a.comitBonLinkedAt}
                        linkedBy={a.comitBonLinkedBy}
                        onDetach={canManage && onBonChanged ? () => detachBon(a.id) : null}
                      />
                    ) : (
                      <>
                        <ComitTaskButton
                          iconOnly
                          texte={buildAlertTaskText(a, {
                            catLabel: ALERT_CAT_LABELS[a.category] || '',
                            sevLabel: ALERT_SEV_LABELS[a.severity] || '',
                          })}
                          comitClientId={a.comitClientId || 0}
                          appareil={a.computerName || ''}
                          cible={{ alertIds: [a.id] }}
                        />
                        {/* Rattachement à un bon DÉJÀ créé. Le cas courant est un
                            bon unique couvrant plusieurs alertes : le créer une
                            fois, puis y rattacher les autres une à une. La
                            sélection multiple reste disponible en haut de page. */}
                        {canManage && onAssignBon && (
                          <button
                            onClick={() => onAssignBon(a)}
                            title="Rattacher à un bon de travail ComIT déjà créé (saisie du numéro)"
                            className={iconBtn('text-blue-400 hover:bg-blue-500/10')}
                          >
                            <Ticket size={13} strokeWidth={2} />
                            <ArrowRight size={10} strokeWidth={2.5} className="-ml-0.5" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
